<!-- --------------------------------------------------------------------------- -->
<!-- Login screen                                                                -->
<!-- --------------------------------------------------------------------------- -->
<template>
  <v-container
    class="touch fill-height fullscreen-center-content"
    fluid
    :style="`z-index:1;background:url('${backgroundImgUrl}');background-size: cover;background-position:${backgroundImgPosition}`"
  >
    <v-form>
      <v-card v-if="!passwordChange && !osWarning && customerFeatures" class="mx-auto elevation-12" :style="`min-width: ${xs ? 300 : 500}px; max-width: 94vw`">
        <v-toolbar color="primary" dark flat>
          <v-toolbar-title v-html="loginTitle"></v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pb-1 pt-3 white">
          <v-select
            v-if="customerRelations.tenants.length > 1"
            :icon-color="iconColor"
            prepend-icon="mdi-warehouse"
            v-model="selectedTenant"
            :dense="xs"
            :items="customerRelations.tenants"
            :label="$t(login.tenant)"
            tabindex="0"
            class="mr-4"
          ></v-select>

          <v-text-field
            :dense="xs"
            v-model="username"
            clearable
            name="jkljkjlkj"
            autocomplete="off"
            :placeholder="$t('login.username')"
            :class="`input-text-light`"
            :icon-color="iconColor"
            tabindex="1"
            prepend-icon="mdi-account"
          ></v-text-field>

          <v-text-field
            ref="password"
            v-model="password"
            autofocus
            clearable
            :placeholder="$t('login.password')"
            :dense="xs"
            name="jkljkJkjj4khh"
            @keyup.enter="login"
            @click:append="revealPassword = !revealPassword"
            :type="revealPassword ? 'text' : 'password'"
            class="input-text-light"
            :icon-color="iconColor"
            prepend-icon="mdi-lock"
            autocomplete="off"
            tabindex="2"
            :append-icon="revealPassword ? 'mdi-eye' : 'mdi-eye-off'"
          />
          <v-spacer />
        </v-card-text>
        <v-card-actions class="py-1 white">
          <v-btn style="height: 35px" :color="theme.textBtn" text v-if="features.passwordForgottenLink" small :href="passwordForgottenLink" target="_blank"
            >{{ $t('login.passwordForgotten') }}
          </v-btn>

          <!--        <v-btn-->
          <!--          style="height: 35px"-->
          <!--          :color="theme.textBtn"-->
          <!--          text-->
          <!--          v-if="features.passwordChangeLink"-->
          <!--          small-->
          <!--          @click="passwordChange = true"-->
          <!--          target="_blank"-->
          <!--          >{{ $t('login.changePassword') }}-->
          <!--        </v-btn>-->
          <v-spacer></v-spacer>
        </v-card-actions>
        <v-card-actions class="white">
          <v-btn color="primary" v-if="hasAzureLogin" @click="azureLogin">
            <v-icon left>mdi-microsoft-azure</v-icon>
            {{ $t('azure.login.btn') }}</v-btn
          >
          <v-spacer />
          <v-btn color="primary" :loading="loading" :disabled="!formIsValid" @click="login">{{ $t('login.btn') }}</v-btn>
        </v-card-actions>
      </v-card>

      <v-dialog v-model="passwordChange" fullscreen hide-overlay transition="dialog-bottom-transition">
        <v-card style="border-radius: 0 !important">
          <v-toolbar dark color="primary">
            <v-btn icon dark @click="passwordChange = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title>{{ $t('changePassword') }}</v-toolbar-title>
          </v-toolbar>
          <iframe :src="passwordChangeLink"></iframe>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar" :timeout="5000" color="error" bottom>{{ errorMsg }}</v-snackbar>

      <v-alert v-model="osWarning" type="error" prominent v-html="$t('alert.iPadForbidden')"></v-alert>
      <v-bottom-sheet v-model="browserWarning">
        <v-sheet class="text-center">
          <v-btn class="mt-6" color="red" text outlined @click="browserWarning = !browserWarning">{{ $t('close') }} </v-btn>
          <div class="pa-6" v-html="$t('alert.browserNotSupported')"></div>
        </v-sheet>
      </v-bottom-sheet>
    </v-form>
  </v-container>
</template>

<script>
import config from '@/common/config';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import { mapState } from 'vuex';
import axiosInitializer from '@/api/axiosInitializer';
import authenticationService from '@/services/authenticationService';

export default {
  name: 'login',

  props: {
    source: String,
  },

  mixins: [common, configMixin],
  mounted() {
    if (this.isIpad && this.iPadIsExcluded) {
      this.osWarning = true;
    }

    const isEdge = /Edge/.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

    if (!isEdge && !isChrome && !isSafari) {
      this.browserWarning = true;
    }
    console.debug('mounted login', config);

    this.apiUrl = config.apiUrl;
    this.selectedTenant = this.customerRelations.tenants[0];
    this.$nextTick(() => {
      this.passwordChange = this.onPasswordChange;
      if (this.userWhenLoggingOut && this.userWhenLoggingOut.userName) {
        this.username = this.userWhenLoggingOut.userName;
      }
    });

    this.interval = setInterval(() => {
      let azureLoginError = localStorage.getItem('azure.login-error');
      if (azureLoginError) {
        this.errorMsg = azureLoginError;
        this.snackbar = true;
        this.$forceUpdate();
        this.$nextTick(() => {
          localStorage.removeItem('azure.login-error');
        });
      }
    }, 1000);
  },
  destroyed() {
    clearInterval(this.interval);
  },
  data() {
    return {
      revealPassword: false,
      selectedTenant: null,
      username: null,
      password: null,
      snackbar: false,
      errorMsg: null,
      passwordChange: false,
      osWarning: false,
      browserWarning: false,
      interval: null,
      loading: false,
    };
  },
  computed: {
    ...mapState({
      configs: (state) => state.configs,
      stage: (state) => state.configs.stage,
      onPasswordChange: (state) => state.profile.onPasswordChange,
      userWhenLoggingOut: (state) => state.profile.userWhenLoggingOut,
      customerRelations: (state) => state.profile.customerRelations,
    }),
    customerFeatures() {
      return this.configs.customerFeatures;
    },
    tenant() {
      return this.customerRelations.tenant;
    },
    customer() {
      return this.customerRelations.customer;
    },
    loginTitle() {
      console.debug('tenant', this.tenant);
      if (this.tenant === 'demoapp') {
        return this.$t('login.title');
      } else {
        return `${this.$t('login.title')} ${this.selectedTenant ? this.selectedTenant.text : ''} ${this.stage !== 'prod' ? '(' + this.stage + ')' : ''}`;
      }
    },
    formIsValid() {
      return true;
      //return this.username && this.username.length >= 3 && this.password && this.password.length > 3;
    },
    backgroundImgUrl() {
      if (!this.configs.customerFeatures.backgroundImg) return null;
      return this.assetUrlUnauthorized(this.configs.customerFeatures.backgroundImg);
    },
    backgroundImgPosition() {
      if (!this.configs.customerFeatures.backgroundImgPosition) return 'center center';
      return this.configs.customerFeatures.backgroundImgPosition;
    },
    isIpad() {
      return /iPad/.test(navigator.userAgent) || (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1);
    },
    iPadIsExcluded() {
      return this.features && this.features.excludedOs && this.features.excludedOs.includes('iPad');
    },
    iconColor() {
      return 'black';
    },
    passwordForgottenLink() {
      return `${this.cockpitUrl(this.stage)}/system/view/forgotpassword.cfm${this.urlParams}`;
    },
    passwordChangeLink() {
      return `${this.cockpitUrl(this.stage)}/system/view/changepassword.cfm${this.urlParams}`;
    },
    urlParams() {
      let userNameParam;
      if (this.userWhenLoggingOut) {
        userNameParam = this.userWhenLoggingOut.userName && `&username=${this.userWhenLoggingOut.userName}`;
        console.debug('found userWhenLoggingOut', userNameParam);
      }
      if (!userNameParam) {
        userNameParam = this.username ? `&username=${this.username}` : '';
        console.debug('use username from login mask', userNameParam);
      }

      return `?mandant=${this.customerRelations.tenant}${userNameParam}`;
    },
    cloudAuthProviderConfig() {
      if (this.features.cloudAuthProvider) {
        if (this.features.cloudAuthProvider[this.stage]) {
          return this.features.cloudAuthProvider[this.stage];
        }
      }
      return {};
    },
    hasAzureLogin() {
      return this.cloudAuthProviderConfig.type && this.cloudAuthProviderConfig.type === 'azure';
    },
  },
  methods: {
    cockpitUrl(stage) {
      switch (stage) {
        case 'dev':
          return process.env.VUE_APP_COCKPIT_URL_DEV;
        case 'alpha':
        case 'test':
          return process.env.VUE_APP_COCKPIT_URL_ALPHA;
        case 'beta':
          return process.env.VUE_APP_COCKPIT_URL_BETA;
        case 'prod':
          return process.env.VUE_APP_COCKPIT_URL_PROD;
        default:
          return process.env.VUE_APP_COCKPIT_URL_DEV;
      }
    },
    async azureLogin() {
      localStorage.setItem('azure.auto_login', true);
      await authenticationService.tryAuthentication(this.cloudAuthProviderConfig, this.features.rememberLogin, true);
    },
    async login() {
      if (!this.formIsValid) {
        return false;
      }
      const onLoginFailed = () => {
        this.loading = false;
        this.errorMsg = this.$t('login.error.invalid');
        this.snackbar = true;
      };

      this.loading = true;
      try {
        localStorage.removeItem('azure.access-token');
        localStorage.removeItem('azure.id-token');

        let response = await this.$store.dispatch('profile/login', {
          tenant: this.selectedTenant.value,
          username: this.username,
          password: this.password,
        });
        axiosInitializer.onSuccessfulLogin();
        this.$store.commit('welcome/setShowWelcome', true);

        console.debug('onSuccessfulLogin');
      } catch (error) {
        if (error.response && error.response.data) {
          onLoginFailed();
          let errorObj = error.response.data;
          console.error('err.response.data', JSON.stringify(errorObj, null, 2)); // => the response payload
          if (errorObj.error === 'AccessDeniedException') {
            this.errorMsg = this.$t('login.error.denied');
          }
          if (errorObj.error === 'PasswordExpiredException') {
            this.passwordChange = true;
            this.errorMsg = this.$t('login.error.expired');
          }
        } else {
          console.error('login failed', error);
          onLoginFailed();
        }
      }
    },
    sendPassword() {
      this.errorMsg = 'not yet implemented';
      this.snackbar = true;
      return false;
    },
  },
};
</script>

<style scoped>
iframe {
  position: absolute;
  width: 100%;
  height: calc(100% - 64px);
  border: 0;
  margin: 0;
  padding: 0;
}

.v-alert.error {
  position: absolute;
  box-shadow: 0 15px 30px -15px rgba(0, 0, 0, 0.75);
}

@media screen and (max-width: 960px) {
  iframe {
    height: calc(100% - 56px);
  }
}
</style>
